/**
 * Strip content out of URLs before they reach a third-party analytics vendor.
 *
 * Our paths are not opaque. A logged-in URL looks like
 *
 *   /a/nora/desk/files/projects/jeldwen-security-review/answers-to-jeldwen.md
 *
 * — customer name, project name and document title, all in the path, all
 * captured by `capture_pageview` as `$current_url` regardless of how thoroughly
 * session replay masks the rendered page. Masking the words on screen and then
 * shipping the filename is not privacy.
 *
 * The rule is subtractive: keep enough shape to tell funnel steps apart
 * (landing → pricing → billing → an agent → that agent's desk), throw away
 * every segment that names a person, a customer, a file or a record. Query
 * strings and fragments go entirely — they carry tokens, emails and pairing
 * codes, and no funnel needs them.
 *
 * Both analytics sinks' adapters live here, beside the scrubbing they share:
 * PostHog's `before_send` takes scrubUrlProperties, Plausible's
 * `transformRequest` takes scrubPlausiblePayload. They were split across the
 * two components once, and Plausible went unscrubbed for three weeks.
 */

import type { PlausibleRequestPayload } from "@plausible-analytics/tracker";

/** Path prefixes whose remaining segments are user data, mapped to how much of
 *  the tail is worth keeping. `/a/<slug>/desk/files/...` collapses to
 *  `/a/:slug/desk`: which tab someone is on is a funnel fact, which file they
 *  opened is not. */
const COLLAPSE: readonly { readonly prefix: string; readonly keepAfter: number }[] = [
  // Instance surfaces: /a/<slug>/<tab>/...
  { prefix: "/a/", keepAfter: 1 },
  // Deployed apps: /apps/<username>/<slug>/...
  { prefix: "/apps/", keepAfter: 0 },
  // Public agent pages: /agents/<slug>
  { prefix: "/agents/", keepAfter: 0 },
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Anything that looks like an identifier rather than a route name. Catches
 *  uuids (admin deep-links), and long opaque segments (tokens, pairing codes)
 *  that are not worth guessing about. */
const isIdentifier = (segment: string): boolean =>
  UUID.test(segment) || (segment.length >= 24 && !segment.includes("."));

/**
 * The privacy-safe form of a pathname. Pure string work so it can be unit
 * tested without a browser or a PostHog client.
 */
export function scrubPathname(pathname: string): string {
  const collapse = COLLAPSE.find((c) => pathname.startsWith(c.prefix));
  if (collapse) {
    const rest = pathname.slice(collapse.prefix.length).split("/").filter((s) => s !== "");
    // The named segment (slug / username) is replaced, never kept: an agent
    // slug is user-chosen and routinely a person's or customer's name.
    const placeholder = collapse.prefix === "/apps/" ? [":username", ":slug"] : [":slug"];
    const kept = rest.slice(placeholder.length, placeholder.length + collapse.keepAfter);
    return collapse.prefix + [...placeholder, ...kept].join("/");
  }
  return pathname
    .split("/")
    .map((segment) => (isIdentifier(segment) ? ":id" : segment))
    .join("/");
}

/**
 * The privacy-safe form of a full URL. Keeps origin and scrubbed path; drops
 * search and hash outright. Returns the input unchanged only when it isn't a
 * URL at all (never silently guesses).
 */
export function scrubUrl(raw: string): string {
  try {
    const url = new URL(raw);
    // Only http(s) have an origin and path worth rebuilding. `new URL` happily
    // parses `mailto:`, `tel:` and `data:`, where `origin` is the STRING
    // "null" and the whole address sits in `pathname` — so rebuilding turns
    // `mailto:info@squidler.io` into `nullinfo@squidler.io`. Those shapes
    // carry no browsing history; leave them as they are.
    if (url.protocol !== "http:" && url.protocol !== "https:") return raw;
    return `${url.origin}${scrubPathname(url.pathname)}`;
  } catch {
    // Not an absolute URL — treat it as a bare path if it looks like one,
    // otherwise leave it alone rather than mangling an unknown shape.
    if (!raw.startsWith("/")) return raw;
    // Drop search and hash HERE too. This branch used to hand the whole string
    // to scrubPathname, so a relative path kept its query — and the promise one
    // paragraph up ("drops search and hash outright") held only for absolute
    // URLs. Nothing noticed while the only relative inputs were PostHog's
    // `$pathname`, which has no query; an `href` prop does.
    return scrubPathname(raw.split(/[?#]/)[0] ?? raw);
  }
}

/** Event properties PostHog fills with URLs. Autocapture sets these on every
 *  `$pageview`/`$pageleave` and on replay metadata, so they are scrubbed
 *  centrally in `before_send` rather than at each call site. */
export const URL_PROPERTIES: readonly string[] = [
  "$current_url",
  "$initial_current_url",
  "$referrer",
  "$initial_referrer",
  "$pathname",
  "$initial_pathname",
];

/** Plausible's tracker names the event URL `u` and the referrer `r`. The
 *  referrer matters as much as the URL: an external one is already public, but
 *  an INTERNAL one is one of our own content URLs and leaks what `u` would
 *  have. Nothing else in the payload carries a URL — it is the event name,
 *  domain and our own custom props — and Plausible drops query strings itself. */
const PLAUSIBLE_URL_PROPERTIES: readonly string[] = ["u", "r"];

/** The keys of `source` that hold a URL string, scrubbed. Only keys actually
 *  present as strings come back, so spreading the result over the source leaves
 *  an absent key absent and a null key null — the invariant both sinks rely on,
 *  stated once here rather than re-derived per adapter. */
const scrubUrlKeys = (
  source: Record<string, unknown>,
  keys: readonly string[],
): Record<string, string> =>
  Object.fromEntries(
    keys
      .filter((key) => typeof source[key] === "string")
      .map((key) => [key, scrubUrl(String(source[key]))]),
  );

/** Scrub every URL-shaped property in place-free fashion, returning a new
 *  object. Non-string values are left untouched. */
export function scrubUrlProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return { ...properties, ...scrubUrlKeys(properties, URL_PROPERTIES) };
}

/** The Plausible counterpart of scrubUrlProperties, for its `transformRequest`
 *  hook — which sends `location.href` verbatim unless this rewrites it. */
export function scrubPlausiblePayload(
  payload: PlausibleRequestPayload,
): PlausibleRequestPayload {
  return { ...payload, ...scrubUrlKeys(payload, PLAUSIBLE_URL_PROPERTIES) };
}
